/**
 * /equipment/cost-postings — list of past equipment cost postings.
 *
 * Filters: period + status. Click a row → detail page where the
 * underlying journal lives.
 */
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useQuery } from "@tanstack/react-query";

import {
  equipmentCostPostingsQueryOptions,
  periodsListQueryOptions,
} from "@/lib/queries";
import type { EquipmentCostPosting } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";
import { useConstructionPack } from "@/lib/useConstructionPack";

export const Route = createFileRoute("/_auth/equipment/cost-postings/")({
  component: PostingsListPage,
});

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined || v === "") return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n as number);
}

function PostingsListPage() {
  const { enabled, loading: packLoading } = useConstructionPack();
  const navigate = useNavigate();

  const [periodId, setPeriodId] = useState<string>("");
  const [status, setStatus] = useState<string>("");

  const periodsQ = useQuery(periodsListQueryOptions());
  const periods = periodsQ.data ?? [];

  const listQ = useQuery(
    equipmentCostPostingsQueryOptions({
      period_id: periodId || undefined,
      status: status || undefined,
      limit: 200,
    }),
  );
  const postings: EquipmentCostPosting[] = listQ.data ?? [];

  if (!packLoading && !enabled) return null;

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <Button
            startIcon={<ArrowBackIcon />}
            size="small"
            onClick={() => navigate({ to: "/equipment" })}
          >
            Back to Equipment
          </Button>
          <Typography variant="h5" fontWeight={600}>
            Equipment cost postings
          </Typography>
        </Stack>
        <HelpHint
          docPath="equipment-costing/posting-to-job-cost.md"
          label="Posting help"
        />
      </Stack>

      <Stack direction="row" spacing={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Period</InputLabel>
          <Select
            value={periodId}
            label="Period"
            onChange={(e) => setPeriodId(e.target.value)}
          >
            <MenuItem value="">All periods</MenuItem>
            {periods.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.start_date} – {p.end_date} ({p.status})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={status}
            label="Status"
            onChange={(e) => setStatus(e.target.value)}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value="posted">Posted</MenuItem>
            <MenuItem value="reversed">Reversed</MenuItem>
          </Select>
        </FormControl>
        <Box sx={{ flex: 1 }} />
        <Button
          variant="contained"
          component={Link}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          to={"/equipment/cost-posting" as any}
        >
          New posting
        </Button>
      </Stack>

      <Paper variant="outlined">
        {listQ.isLoading && (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {listQ.error && <Alert severity="error">Failed to load postings.</Alert>}
        {!listQ.isLoading && postings.length === 0 && (
          <Typography variant="body2" sx={{ p: 3, color: "text.secondary" }}>
            No postings yet. Create one via the New posting button or
            Equipment → Cost Posting.
          </Typography>
        )}
        {postings.length > 0 && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Posting date</TableCell>
                <TableCell align="right">Lines</TableCell>
                <TableCell align="right">Ownership</TableCell>
                <TableCell align="right">Operating</TableCell>
                <TableCell align="right">Transport</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Journal</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {postings.map((p) => (
                <TableRow
                  key={p.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() =>
                    navigate({
                      to: "/equipment/cost-postings/$postingId",
                      params: { postingId: p.id },
                    })
                  }
                >
                  <TableCell>{p.posting_date}</TableCell>
                  <TableCell align="right">{p.line_count}</TableCell>
                  <TableCell align="right">
                    {fmtMoney(p.total_ownership)}
                  </TableCell>
                  <TableCell align="right">
                    {fmtMoney(p.total_operating)}
                  </TableCell>
                  <TableCell align="right">
                    {fmtMoney(p.total_transport)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {fmtMoney(p.total_charge_out)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={p.status}
                      color={p.status === "posted" ? "success" : "warning"}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                    <Link
                      to="/journals/$journalId"
                      params={{ journalId: p.journal_id }}
                      onClick={(e) => e.stopPropagation()}
                      style={{ color: "inherit" }}
                    >
                      {p.journal_id.slice(0, 8)}…
                    </Link>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Box>
  );
}
